/**
 * PATH: apps/qa/src/pages/signoffs-page/ApprovalModal.tsx
 * PURPOSE: Modal for approving a hold point with signature capture
 */

import { useState } from 'react'
import { X, CheckCircle, Loader2, AlertTriangle } from 'lucide-react'
import api from '@/lib/api'
import { SignaturePad } from './SignaturePad'
import type { HoldPoint, SignerDetails } from './types'

interface ApprovalModalProps {
  holdPoint: HoldPoint
  onClose: () => void
  onApprove: (signatureAssetId: string, signerDetails: SignerDetails) => void
  isSubmitting: boolean
}

export function ApprovalModal({ holdPoint, onClose, onApprove, isSubmitting }: ApprovalModalProps) {
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [signerName, setSignerName] = useState('')
  const [signerRole, setSignerRole] = useState('')
  const [signerEmail, setSignerEmail] = useState('')
  const [signerCompany, setSignerCompany] = useState('')
  const [uploadingSignature, setUploadingSignature] = useState(false)

  const handleApprove = async () => {
    if (!signatureDataUrl || !signerName) return
    setUploadingSignature(true)
    try {
      const response = await fetch(signatureDataUrl)
      const blob = await response.blob()
      const file = new File([blob], 'signature.png', { type: 'image/png' })
      const formData = new FormData()
      formData.append('file', file); formData.append('purpose', 'qa_signature')
      const uploadResponse = await api().post('/files/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      onApprove(uploadResponse.data.id, { signer_name: signerName, signer_role: signerRole, signer_email: signerEmail, signer_company: signerCompany })
    } catch (err) { if (import.meta.env.DEV) console.error('Failed to upload signature:', err) }
    finally { setUploadingSignature(false) }
  }

  const canApprove = signatureDataUrl && signerName.trim()
  const inputCls = "w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-lg shadow-xl border border-border animate-fadeIn max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Approve Hold Point</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 rounded-lg bg-muted/50 mb-6">
          <p className="font-medium text-foreground">{holdPoint.name}</p>
          {Array.isArray(holdPoint.required_signer_roles) && holdPoint.required_signer_roles.length > 0 && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 mt-2 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"><AlertTriangle className="w-3 h-3" />Critical Hold Point</span>
          )}
        </div>
        <div className="space-y-4 mb-6">
          <div><label className="block text-sm font-medium text-foreground mb-1.5">Your Name <span className="text-destructive">*</span></label><input type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name" className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-foreground mb-1.5">Role</label><input type="text" value={signerRole} onChange={(e) => setSignerRole(e.target.value)} placeholder="e.g., Site Supervisor" className={inputCls} /></div>
            <div><label className="block text-sm font-medium text-foreground mb-1.5">Company</label><input type="text" value={signerCompany} onChange={(e) => setSignerCompany(e.target.value)} placeholder="Company name" className={inputCls} /></div>
          </div>
          <div><label className="block text-sm font-medium text-foreground mb-1.5">Email</label><input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder="your@email.com" className={inputCls} /></div>
        </div>
        <div className="mb-6"><label className="block text-sm font-medium text-foreground mb-2">Signature <span className="text-destructive">*</span></label><SignaturePad onSignatureCapture={setSignatureDataUrl} onClear={() => setSignatureDataUrl(null)} /></div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
          <button onClick={handleApprove} disabled={!canApprove || isSubmitting || uploadingSignature} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {(isSubmitting || uploadingSignature) ? <><Loader2 className="w-4 h-4 animate-spin" />Signing...</> : <><CheckCircle className="w-4 h-4" />Sign & Approve</>}
          </button>
        </div>
      </div>
    </div>
  )
}
